import { useEffect, useMemo, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate } from 'react-router-dom';
import { Card, Chip, Pane, PaneHead } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import { useMembers } from '../lib/useManagerData';
import { useShelf } from '@/pages/roles/stock-manager/lib/useStock';
import { SettingsRail } from './SettingsRail';
import { SettingsUnreadable } from './Unreadable';
import { MODULES, MODULE_FLOOR, type ModuleDef } from './modules';
import { useModuleSettings, useProviderCount, useSaveModuleSettings } from './useSettings';

/**
 * Clinic manager · Settings ▸ Modules — `g_set_mod.dc.html`.
 *
 * THE PANE IS THE SELECTION and the board says what it is for: "WHAT OFF WOULD
 * MEAN". So every row is clickable, the pane re-docks on the row you clicked,
 * and the first card in it is the consequence — or, when the module is not
 * built here, the refusal, which is the shape the board itself draws for Stock.
 *
 * A SWITCH THAT HIDES NOTHING IS NOT A SWITCH. The design's module set and this
 * product's are not the same set (see ./modules.ts): four of the eight are
 * surfaces that exist and four are not built at all. The four that exist write
 * to `system_settings['modules.enabled']`, the map the EMR already reads on
 * boot; the four that do not are drawn off and refused, because storing a
 * choice nothing reads would be a control that says it did something (§4.8).
 */
export function ManagerModulesPage() {
  const go = useNavigate();
  const { value, isLoading, error, refetch } = useModuleSettings();
  const save = useSaveModuleSettings();
  const providers = useProviderCount();
  const { members } = useMembers();
  const [draft, setDraft] = useState<Record<string, boolean> | null>(null);
  const [opened, setOpened] = useState<string>(MODULES[0].key);
  /* Phone only: the desk always shows one module's pane, a phone opens it on a
     tap of the card — not of its switch, which changes the module in place. */
  const [tapped, setTapped] = useState(false);
  const [query, setQuery] = useState('');

  usePageMeta({ title: 'Modules · System Settings' });

  /* THE STORED MAP IS SPARSE. A clinic that never opened this screen has {},
     which means "has not chosen" — the EMR then falls back to its defaults, and
     a built module's default is on. So the draft is the stored map laid over
     "every built module on", never the bare map. */
  useEffect(() => {
    if (!value || draft) return;
    const seeded: Record<string, boolean> = {};
    for (const m of MODULES) seeded[m.key] = m.built ? (value[m.key] ?? true) : false;
    setDraft(seeded);
  }, [value, draft]);

  const dirty = useMemo(() => {
    if (!draft || !value) return false;
    return MODULES.some((m) => m.built && draft[m.key] !== (value[m.key] ?? true));
  }, [draft, value]);

  const reading = isLoading && !value;
  const unreadable = !reading && !value;

  const nurses = members.filter((m) => m.roleName === 'Nurse' && m.status === 'active').length;
  const technicians = members.filter((m) => m.roleName === 'Technician' && m.status === 'active').length;
  const stockManagers = members.filter((m) => m.roleName === 'Stock Manager' && m.status === 'active').length;
  const { data: shelf } = useShelf();

  /** The board's caption, or the live figure that replaces it. */
  const captionOf = (m: ModuleDef): string => {
    if (m.live === 'nurses') return nurses === 1 ? '1 nurse' : `${nurses} nurses`;
    if (m.live === 'technicians') return technicians === 1 ? '1 technician' : `${technicians} technicians`;
    if (m.live === 'stock') {
      const sm = stockManagers === 1 ? '1 stock manager' : `${stockManagers} stock managers`;
      return shelf ? `${shelf.length} items · ${sm}` : sm;
    }
    if (m.live === 'providers') {
      return providers === null ? 'external providers' : `${providers} external providers`;
    }
    return m.sub;
  };

  const q = query.trim().toLowerCase();
  const shown = MODULES.filter((m) => !q || `${m.title} ${captionOf(m)}`.toLowerCase().includes(q));
  const selected = MODULES.find((m) => m.key === opened) ?? MODULES[0];

  const pane = (
    <Pane head={(
      <PaneHead overline="WHAT OFF WOULD MEAN" title={selected.title} sub={captionOf(selected)} />
    )}>
      <>
        {selected.built ? (
          <Card>
            <p className="t-sec sec">{selected.off}</p>
          </Card>
        ) : (
          <Card style={{ borderColor: 'var(--danger-600)', background: 'var(--danger-100)' }}>
            <p className="t-sec w6" style={{ color: 'var(--danger-600)' }}>
              Cannot turn off {selected.title.split(' ')[0]}
            </p>
            <p className="t-cap sec mt4">{selected.off}</p>
          </Card>
        )}
        <Card>
          <p className="t-sec sec">{selected.keeps}</p>
        </Card>
        <Card kind="flat" style={{ background: 'var(--info-100)' }}>
          <p className="t-cap mut">{MODULE_FLOOR}</p>
        </Card>
      </>
    </Pane>
  );

  const crumb = usePhoneCrumb('Settings', 'Modules');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      phoneMenu: (close) => <ManagerMenu door="settings" onClose={close} />,
      paneOpen: tapped,
      onPaneClose: () => setTapped(false),
      paneTitle: selected.title,
      title: 'Modules',
      sub: (
        <>
          <button type="button" className="w6 brand"
            style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={() => go('/clinic-manager/settings')}>
            ‹ System Settings
          </button>
          {' '}<span className="mut">▸</span> Modules
        </>
      ),
      search: unreadable ? null : 'Search a setting…',
      searchValue: query,
      onSearchChange: setQuery,
      subrail: <SettingsRail active="modules" />,
      extra: dirty ? <Chip kind="warn" small className="w6">⚠ unsaved</Chip> : undefined,
      primary: draft ? {
        label: save.isPending ? 'Saving…' : 'Save',
        /* A full-set write, as the endpoint documents — and only of the keys
           this screen owns, so a module some other screen switched is not
           blanked by a save here. */
        onClick: () => save.mutate({
          ...(value ?? {}),
          ...Object.fromEntries(MODULES.filter((m) => m.built).map((m) => [m.key, draft[m.key]])),
        }),
      } : undefined,
      pane: unreadable ? undefined : pane,
    },
    [tapped, query, dirty, save.isPending, !!draft, unreadable, opened, selected.title, nurses, technicians, stockManagers, shelf?.length ?? -1, providers ?? -1],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        <p className="t-sec sec" style={{ margin: '4px 0 12px' }}>
          Turn a module off when the clinic tried it and stopped — the vanish rule only covers what
          was never started. Off means hidden everywhere: nav, home, reports, bubbles. Nothing is
          deleted.
        </p>

        {reading ? (
          <p className="t-sec mut" style={{ marginTop: 18 }}>Reading this clinic’s modules…</p>
        ) : null}

        {unreadable ? (
          <SettingsUnreadable what="modules" error={error} onRetry={() => { void refetch(); }} />
        ) : null}

        {draft ? shown.map((m) => {
          const on = draft[m.key];
          return (
            <Card key={m.key}
              kind={m.key === opened ? 'accent' : undefined}
              style={{ cursor: 'pointer' }}
              onClick={() => { setOpened(m.key); setTapped(true); }}>
              <div className="flex" style={{ gap: 12 }}>
                <div className="grow">
                  <p className="t-strong">{m.title}</p>
                  <p className="t-cap mut">{captionOf(m)}</p>
                </div>
                {/* The kit's `.tgl` is the visual; the input carries the state
                    and the keyboard. An unbuilt module's switch is refused
                    rather than drawn live — pressing it would store a choice
                    nothing reads. */}
                <label onClick={(e) => e.stopPropagation()}
                  style={{ display: 'inline-flex', cursor: m.built ? 'pointer' : 'not-allowed' }}>
                  <input type="checkbox" className="sr-only" checked={on} disabled={!m.built}
                    aria-label={`${m.title} — ${on ? 'on' : 'off'}`}
                    onChange={(e) => {
                      setOpened(m.key);
                      setDraft((d) => (d ? { ...d, [m.key]: e.target.checked } : d));
                    }} />
                  <span className={on ? 'tgl on' : 'tgl'} aria-hidden="true" />
                </label>
              </div>
            </Card>
          );
        }) : null}

        {draft && draft.flows === false ? (
          <Card style={{ background: 'var(--warning-100)' }}>
            <p className="t-sec w6" style={{ color: 'var(--warning-600)' }}>
              Flows is off — saved flows are kept
            </p>
            <p className="t-cap mut mt4">Turning it back on restores them exactly.</p>
          </Card>
        ) : null}

        {draft && !shown.length ? (
          <p className="t-sec mut" style={{ marginTop: 18 }}>
            No module matches that. Clear the search to see all eight again.
          </p>
        ) : null}

        {save.isError ? (
          <p className="t-sec red" style={{ marginTop: 10 }}>
            That did not save. Nothing was changed on the server — try again, or reload to see
            what is stored.
          </p>
        ) : null}
        {save.isSuccess && !dirty ? <p className="t-sec grn" style={{ marginTop: 10 }}>Saved.</p> : null}
      </div>
    </div>
  );
}
